import AddIcon from "@mui/icons-material/Add";
import TabletAndroidOutlinedIcon from "@mui/icons-material/TabletAndroidOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { kioskStaffApi } from "@bw/shared/api";
import type {
  KioskDevice,
  KioskDeviceCreated,
  KioskMode,
} from "@bw/shared/types/generated";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { useMyFacilityActions } from "@/hooks/use-my-facility-actions";
import { useResolvedFacility } from "@/hooks/use-selected-facility";
import { createZonedTimeFormatter } from "@/libs/zonedDateTime";

import {
  KIOSK_DEVICE_STATUS_LABEL,
  KIOSK_MODE_LABEL,
  kioskDeviceLine,
  kioskDeviceStatus,
} from "./kiosk-display";
import {
  KioskSetupCode,
  StartKioskDialog,
  kioskErrorMessage,
} from "./kiosk-pairing-dialog";

const MODES: KioskMode[] = ["automatic", "staff_confirms", "off"];
const MODE_HELP: Record<KioskMode, string> = {
  automatic: "A green scan checks the player in.",
  staff_confirms: "A drop-in scan waits for a person at the desk.",
  off: "Kiosks are revoked and scans are refused.",
};

const visuallyHidden = {
  position: "absolute",
  width: "1px",
  height: "1px",
  p: 0,
  m: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;

/** Front desk › Devices (#2691): the facility's scan kiosks and the kiosk switch. */
export function KioskDevicesPage() {
  const { facility, facilityId, isLoading } = useResolvedFacility();
  if (isLoading || !facilityId) {
    return (
      <Box role="status" sx={{ px: { xs: 2, lg: 3 }, py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          Loading devices…
        </Typography>
      </Box>
    );
  }
  const timeZone = facility?.timezone || "America/Los_Angeles";
  return (
    <FacilityDevices
      key={`${facilityId}:${timeZone}`}
      facilityId={facilityId}
      facilityName={facility?.name ?? "This facility"}
      timeZone={timeZone}
    />
  );
}

type Pending =
  | { kind: "revoke"; device: KioskDevice }
  | { kind: "repair"; device: KioskDevice }
  | { kind: "off"; active: number };

function FacilityDevices({
  facilityId,
  facilityName,
  timeZone,
}: {
  facilityId: string;
  facilityName: string;
  timeZone: string;
}) {
  const queryClient = useQueryClient();
  const { actions } = useMyFacilityActions(facilityId);
  const canManage = actions.includes("facility:manage");
  const timeFormatter = useMemo(
    () => createZonedTimeFormatter(timeZone),
    [timeZone],
  );
  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat("en-US", {
        timeZone,
        month: "short",
        day: "numeric",
      }),
    [timeZone],
  );

  const devicesKey = ["kiosk-devices", facilityId];
  const settingsKey = ["kiosk-settings", facilityId];
  const devices = useQuery({
    queryKey: devicesKey,
    queryFn: () => kioskStaffApi.listDevices(facilityId),
    refetchInterval: 30_000,
  });
  const settings = useQuery({
    queryKey: settingsKey,
    queryFn: () => kioskStaffApi.getSettings(facilityId),
  });

  const [startOpen, setStartOpen] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const [repaired, setRepaired] = useState<KioskDeviceCreated | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mode = settings.data?.mode ?? null;
  const list = devices.data ?? [];
  const active = list.filter((d) => d.state === "active");
  const revoked = list.filter((d) => d.state === "revoked");

  const refreshDevices = () =>
    void queryClient.invalidateQueries({ queryKey: devicesKey });

  const act = async (work: () => Promise<void>) => {
    setWorking(true);
    setError(null);
    try {
      await work();
      setPending(null);
    } catch (cause) {
      setError(kioskErrorMessage(cause));
      setPending(null);
    } finally {
      setWorking(false);
    }
  };

  const setMode = (next: KioskMode) =>
    act(async () => {
      const saved = await kioskStaffApi.updateSettings(facilityId, {
        mode: next,
      });
      queryClient.setQueryData(settingsKey, saved);
      refreshDevices();
    });

  const chooseMode = (next: KioskMode | null) => {
    if (!next || next === mode || working) return;
    if (next === "off" && active.length > 0) {
      setPending({ kind: "off", active: active.length });
      return;
    }
    void setMode(next);
  };

  const revoke = (device: KioskDevice) =>
    act(async () => {
      await kioskStaffApi.revokeDevice(facilityId, device.device_id);
      refreshDevices();
    });

  const repair = (device: KioskDevice) =>
    act(async () => {
      setRepaired(
        await kioskStaffApi.newSetupCode(facilityId, device.device_id),
      );
      refreshDevices();
    });

  const askRepair = (device: KioskDevice) => {
    if (device.online) setPending({ kind: "repair", device });
    else void repair(device);
  };

  const kioskOff = mode === "off";

  return (
    <Box
      sx={{
        px: { xs: 2, lg: 3 },
        pb: 3,
        display: "grid",
        gap: 1.5,
        maxWidth: 960,
      }}
    >
      <Typography component="h1" sx={visuallyHidden}>
        Devices
      </Typography>
      <Box
        role="toolbar"
        aria-label="Kiosk tools"
        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 700 }}
          id="kiosk-mode-label"
        >
          Kiosk check-in
        </Typography>
        {settings.isLoading ? (
          <Skeleton width={260} height={32} />
        ) : (
          <Tooltip
            title={
              canManage
                ? ""
                : "Changing this needs Facility settings permission"
            }
            disableHoverListener={canManage}
          >
            <span>
              <ToggleButtonGroup
                exclusive
                size="small"
                color="primary"
                value={mode}
                onChange={(_, next: KioskMode | null) => chooseMode(next)}
                aria-labelledby="kiosk-mode-label"
                disabled={!canManage || working || settings.isError}
              >
                {MODES.map((value) => (
                  <ToggleButton
                    key={value}
                    value={value}
                    sx={{
                      px: 1.5,
                      py: 0.375,
                      minHeight: { xs: 44, md: 32 },
                      typography: "body2",
                    }}
                  >
                    {KIOSK_MODE_LABEL[value]}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </span>
          </Tooltip>
        )}
        {mode ? (
          <Typography variant="caption" color="text.secondary">
            {MODE_HELP[mode]}
          </Typography>
        ) : null}
        <Tooltip
          title={kioskOff ? "Turn kiosk check-in on first" : ""}
          disableHoverListener={!kioskOff}
        >
          <Box component="span" sx={{ ml: "auto" }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              onClick={() => setStartOpen(true)}
              disabled={kioskOff || settings.isLoading}
              sx={{ minHeight: { xs: 44, md: 32 } }}
            >
              Start scan kiosk
            </Button>
          </Box>
        </Tooltip>
      </Box>

      {settings.isError ? (
        <Alert severity="error">
          The kiosk setting is unavailable. Try again.
        </Alert>
      ) : null}
      {error ? (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Paper
        component="section"
        aria-labelledby="kiosk-devices-title"
        variant="outlined"
        sx={{ overflow: "hidden" }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1,
            display: "flex",
            alignItems: "baseline",
            gap: 1,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Typography
            id="kiosk-devices-title"
            component="h2"
            variant="subtitle2"
            sx={{ fontWeight: 700 }}
          >
            Devices
          </Typography>
          <Typography variant="caption" color="text.secondary">
            kiosks for {facilityName}
          </Typography>
        </Box>
        {devices.isLoading ? (
          <Box
            sx={{ px: 1.5, py: 1 }}
            role="status"
            aria-label="Loading devices"
          >
            <Skeleton height={44} />
            <Skeleton height={44} />
          </Box>
        ) : devices.isError ? (
          <Alert severity="error" sx={{ m: 1.5 }}>
            Devices are unavailable. Try again.
          </Alert>
        ) : list.length === 0 ? (
          <Box sx={{ px: 1.5, py: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              No kiosks yet.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {kioskOff
                ? "Turn kiosk check-in on, then start a scan kiosk on the entrance tablet."
                : "Start a scan kiosk on the entrance tablet so players check themselves in."}
            </Typography>
          </Box>
        ) : (
          <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
            {[...active, ...revoked].map((device, index) => (
              <DeviceRow
                key={device.device_id}
                device={device}
                divided={index > 0}
                line={kioskDeviceLine(device, timeFormatter, dateFormatter)}
                disabled={working || kioskOff}
                onRepair={() => askRepair(device)}
                onRevoke={() => setPending({ kind: "revoke", device })}
              />
            ))}
          </Box>
        )}
      </Paper>

      <StartKioskDialog
        open={startOpen}
        facilityId={facilityId}
        facilityName={facilityName}
        timeFormatter={timeFormatter}
        onClose={() => setStartOpen(false)}
        onCreated={refreshDevices}
      />

      <Dialog
        open={repaired !== null}
        onClose={() => setRepaired(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ typography: "subtitle1", fontWeight: 700 }}>
          {repaired ? `Pair ${repaired.device.name}` : "Pair kiosk"}
        </DialogTitle>
        <DialogContent dividers>
          {repaired ? (
            <KioskSetupCode created={repaired} timeFormatter={timeFormatter} />
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button variant="contained" onClick={() => setRepaired(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        pending={pending}
        working={working}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (!pending) return;
          if (pending.kind === "revoke") void revoke(pending.device);
          else if (pending.kind === "repair") void repair(pending.device);
          else void setMode("off");
        }}
      />
    </Box>
  );
}

function DeviceRow({
  device,
  divided,
  line,
  disabled,
  onRepair,
  onRevoke,
}: {
  device: KioskDevice;
  divided: boolean;
  line: string;
  disabled: boolean;
  onRepair: () => void;
  onRevoke: () => void;
}) {
  const status = kioskDeviceStatus(device);
  const tone =
    status === "online"
      ? { color: "primary.main", bgcolor: "action.selected" }
      : { color: "text.secondary", bgcolor: "action.hover" };
  return (
    <Box
      component="li"
      data-testid="kiosk-device-row"
      sx={{
        px: 1.5,
        py: 1,
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        flexWrap: { xs: "wrap", sm: "nowrap" },
        borderTop: divided ? 1 : 0,
        borderColor: "divider",
      }}
    >
      <TabletAndroidOutlinedIcon
        fontSize="small"
        sx={{ color: "text.secondary" }}
      />
      <Box
        sx={{
          minWidth: 0,
          flex: { xs: "1 1 calc(100% - 44px)", sm: 1 },
          opacity: status === "revoked" ? 0.6 : 1,
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, overflowWrap: "anywhere" }}
        >
          {device.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {line}
        </Typography>
      </Box>
      <Box
        component="span"
        sx={{
          px: 0.75,
          py: 0.25,
          ml: { xs: 4, sm: 0 },
          borderRadius: 1,
          typography: "caption",
          fontWeight: 600,
          ...tone,
        }}
      >
        {KIOSK_DEVICE_STATUS_LABEL[status]}
      </Box>
      {status === "revoked" ? null : (
        <Box sx={{ display: "flex", gap: 0.75 }}>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={onRepair}
            disabled={disabled}
            aria-label={`Re-pair ${device.name}`}
            sx={{ minHeight: { xs: 44, md: 32 }, borderColor: "divider" }}
          >
            Re-pair
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={onRevoke}
            disabled={disabled}
            aria-label={`Revoke ${device.name}`}
            sx={{ minHeight: { xs: 44, md: 32 } }}
          >
            Revoke
          </Button>
        </Box>
      )}
    </Box>
  );
}

function ConfirmDialog({
  pending,
  working,
  onCancel,
  onConfirm,
}: {
  pending: Pending | null;
  working: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const copy =
    pending?.kind === "revoke"
      ? {
          title: `Revoke ${pending.device.name}?`,
          body: "The tablet stops scanning right away and has to be started again to come back.",
          action: "Revoke",
        }
      : pending?.kind === "repair"
        ? {
            title: `Re-pair ${pending.device.name}?`,
            body: "The tablet is online. Re-pairing signs it out now; it scans again once you pair it with the new code.",
            action: "Re-pair",
          }
        : pending?.kind === "off"
          ? {
              title: "Turn kiosk check-in off?",
              body: `This revokes ${pending.active === 1 ? "the kiosk" : `all ${pending.active} kiosks`}. Turning it back on needs each tablet started again.`,
              action: "Turn off",
            }
          : null;
  return (
    <Dialog open={pending !== null} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ typography: "subtitle1", fontWeight: 700 }}>
        {copy?.title}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2">{copy?.body}</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onCancel} disabled={working}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={pending?.kind === "repair" ? "primary" : "error"}
          onClick={onConfirm}
          disabled={working}
        >
          {copy?.action}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
